import {
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import {
  ArrowPathIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  useGetArtifactAuditQuery,
  useRerunArtifactAuditMutation,
  useSubmitArtifactAuditReviewMutation,
} from '@/services/api/artifactAuditService';
import { ArtifactAudit, ArtifactAuditStatus } from '@/services/types/blob-types';

interface AuditIndicatorProps {
  audit: ArtifactAudit;
  artifactId: string;
  matterId: string;
}

const POLLING_STATUSES: ArtifactAuditStatus[] = ['PENDING', 'RUNNING'];

/**
 * Badge config per status, matching the Paper design specs exactly.
 * Processing: blue-50 bg, blue-500 text, sparkle icon
 * Flagged: amber-100 (#FEF3C7) bg, amber-600 (#D97706) text, warning icon
 * Failed: red-50 bg, red-500 text, no badge icon (dot in popover)
 */
const getStatusConfig = (status: ArtifactAuditStatus) => {
  switch (status) {
    case 'PENDING':
    case 'RUNNING':
      return {
        badgeBg: 'bg-blue-50',
        badgeText: 'text-blue-500',
        badgeLabel: 'Analyzing',
        popoverLabel: 'Analyzing...',
        popoverLabelColor: '',
        dotColor: 'bg-blue-400',
      };
    case 'FLAGGED_PENDING_REVIEW':
      return {
        badgeBg: 'bg-[#FEF3C7]',
        badgeText: 'text-[#D97706]',
        badgeLabel: 'Flagged',
        popoverLabel: 'Flagged for review',
        popoverLabelColor: 'text-amber-700',
        dotColor: 'bg-amber-400',
      };
    case 'FAILED':
      return {
        badgeBg: 'bg-red-50',
        badgeText: 'text-red-500',
        badgeLabel: 'Failed',
        popoverLabel: 'Audit failed',
        popoverLabelColor: 'text-red-600',
        dotColor: 'bg-red-400',
      };
    default:
      return null;
  }
};

const BadgeIcon: React.FC<{ status: ArtifactAuditStatus; className?: string }> = ({
  status,
  className,
}) => {
  if (status === 'PENDING' || status === 'RUNNING') {
    return <SparklesIcon className={className} />;
  }
  if (status === 'FLAGGED_PENDING_REVIEW') {
    return <ExclamationTriangleIcon className={className} />;
  }
  return <ArrowPathIcon className={className} />;
};

export const AuditIndicator: React.FC<AuditIndicatorProps> = ({
  audit: initialAudit,
  artifactId,
  matterId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteNotes, setShowDeleteNotes] = useState(false);
  const [deleteNotes, setDeleteNotes] = useState('');
  const [shouldPoll, setShouldPoll] = useState(
    POLLING_STATUSES.includes(initialAudit.status),
  );

  const { data: polledAudit } = useGetArtifactAuditQuery(
    { artifactId },
    {
      pollingInterval: shouldPoll ? 5000 : 0,
      skip: !shouldPoll,
      skipPollingIfUnfocused: true,
    },
  );

  useEffect(() => {
    if (polledAudit && !POLLING_STATUSES.includes(polledAudit.status)) {
      setShouldPoll(false);
    }
  }, [polledAudit]);

  const audit = polledAudit ?? initialAudit;

  const [rerunAudit, { isLoading: isRerunning }] =
    useRerunArtifactAuditMutation();
  const [submitReview, { isLoading: isSubmitting }] =
    useSubmitArtifactAuditReviewMutation();

  const isLoading = isRerunning || isSubmitting;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 10 })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  const statusConfig = getStatusConfig(audit.status);

  if (!statusConfig) return null;

  const handleKeep = async () => {
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'override_safe', notes: 'Marked safe by reviewer' },
      }).unwrap();
      toast.success('Document marked as safe');
      setIsOpen(false);
    } catch {
      toast.error('Failed to submit review');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteNotes) {
      setShowDeleteNotes(true);
      return;
    }
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'confirm_mismatch', notes: deleteNotes || 'Confirmed mismatch by reviewer' },
      }).unwrap();
      toast.success('Document confirmed as mismatch');
      setIsOpen(false);
      setShowDeleteNotes(false);
      setDeleteNotes('');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  const handleRetry = async () => {
    try {
      await rerunAudit({ artifactId, matterId }).unwrap();
      toast.success('Audit restarted');
      setShouldPoll(true);
      setIsOpen(false);
    } catch {
      toast.error('Failed to rerun audit');
    }
  };

  const isFlagged = audit.status === 'FLAGGED_PENDING_REVIEW';
  const isFailed = audit.status === 'FAILED';
  const mismatches = audit.result?.mismatches ?? [];

  return (
    <>
      {/* Pill badge trigger — matches Paper design: rounded-full, px-2 py-[2px], gap-1, 9px/600 text */}
      <button
        ref={refs.setReference}
        type="button"
        className={`flex items-center gap-1 px-2 py-[2px] rounded-full flex-shrink-0 hover:opacity-80 transition-opacity ${statusConfig.badgeBg}`}
        onClick={(e) => e.stopPropagation()}
        title={statusConfig.popoverLabel}
        {...getReferenceProps()}
      >
        <BadgeIcon
          status={audit.status}
          className={`w-2.5 h-2.5 flex-shrink-0 ${statusConfig.badgeText}`}
        />
        <span
          className={`text-[9px] font-semibold leading-3 whitespace-nowrap flex-shrink-0 ${statusConfig.badgeText}`}
        >
          {statusConfig.badgeLabel}
        </span>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            role="dialog"
            className="z-[9999] w-80 bg-white border border-slate-200 rounded-lg shadow-lg p-3"
          >
            <div className="space-y-2">
              {/* Status header */}
              <div className="flex items-center gap-2">
                <span
                  className={`block w-2 h-2 rounded-full ${statusConfig.dotColor}`}
                />
                <span
                  className={`font-medium text-sm ${statusConfig.popoverLabelColor}`}
                >
                  {statusConfig.popoverLabel}
                </span>
              </div>

              {/* Mismatch details for flagged */}
              {isFlagged && mismatches.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {mismatches.map((mismatch, i) => (
                    <div
                      key={i}
                      className="text-xs bg-amber-50 border border-amber-200 rounded p-2"
                    >
                      <p className="font-medium text-amber-800">
                        {mismatch.reasoning}
                      </p>
                      {mismatch.evidence.map((ev, j) => (
                        <p key={j} className="mt-1 text-amber-700">
                          {ev.summary}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {/* Confidence */}
              {audit.confidence != null && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Confidence: </span>
                  <span className="font-medium">
                    {Math.round(audit.confidence * 100)}%
                  </span>
                </div>
              )}

              {/* Failure reason */}
              {isFailed && audit.failure_reason && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Error: </span>
                  <span className="font-medium text-red-600">
                    {audit.failure_reason}
                  </span>
                </div>
              )}

              {/* Delete notes textarea */}
              {showDeleteNotes && (
                <textarea
                  className="w-full text-xs border border-slate-200 rounded p-2 resize-none"
                  rows={2}
                  placeholder="Optional notes..."
                  value={deleteNotes}
                  onChange={(e) => setDeleteNotes(e.target.value)}
                />
              )}

              {/* Keep / Delete actions for flagged */}
              {isFlagged && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 border-slate-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-200"
                    onClick={handleKeep}
                    disabled={isLoading}
                  >
                    <CheckIcon className="w-3.5 h-3.5 mr-1.5" />
                    Keep
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 border-slate-200 text-rose-700 hover:bg-rose-50 hover:border-rose-200"
                    onClick={handleDelete}
                    disabled={isLoading}
                  >
                    <TrashIcon className="w-3.5 h-3.5 mr-1.5" />
                    {showDeleteNotes ? 'Confirm' : 'Delete'}
                  </Button>
                </div>
              )}

              {/* Retry action for failed */}
              {isFailed && (
                <div className="flex items-center gap-2 pt-2 mt-1 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 border-slate-200 text-blue-700 hover:bg-blue-50 hover:border-blue-200"
                    onClick={handleRetry}
                    disabled={isLoading}
                  >
                    <ArrowPathIcon className="w-3.5 h-3.5 mr-1.5" />
                    Retry Audit
                  </Button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
